<template>
  <div
    class="editor-floating-buttons"
    :style="{
      // Position floating buttons in the margin above the board, aligned to the right
      left: isRtl && `${floatingButtonHorizontalPosition}px`,
      right: isRtl || `${floatingButtonHorizontalPosition}px`,
      top: `${floatingButtonVerticalPosition / 2}px`,
    }"
  >
    <NebulaTooltip
      class="editor-floating-buttons__button-wrapper"
      position="bottom"
      :text="$t('Print Preview')"
    >
      <a
        class="nebula-button nebula-button--icon-only nebula-button--m
          editor-floating-buttons__button editor-floating-buttons__button--print"
        target="_blank"
        :aria-label="$t('Print Preview')"
        :href="printPreviewLocation"
      >
        <NebulaIcon
          symbol-id="print"
          class-override="nebula-button__icon"
        />
      </a>
    </NebulaTooltip>
    <ImmersiveReader
      class="editor-floating-buttons__button-wrapper"
      v-if="showImmersiveReader"
      :mode="mode"
      use-floating-style
    />
    <NebulaTooltip
      class="editor-floating-buttons__button-wrapper"
      position="bottom"
      :text="$t('Full Screen')"
    >
      <button
        class="nebula-button nebula-button--icon-only nebula-button--m
          editor-floating-buttons__button editor-floating-buttons__button--fullscreen"
        :aria-label="$t('Full Screen')"
        @click="showFullscreen"
      >
        <NebulaIcon
          symbol-id="expand"
          class-override="nebula-button__icon"
        />
      </button>
    </NebulaTooltip>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { NebulaIcon, NebulaTooltip } from '@discoveryedu/nebula-components';
import ImmersiveReader from '@/components/ImmersiveReader.vue';
import { bus } from '@/lib/eventBus';
import {
  useEditorStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'EditorFloatingButtons',
  components: {
    ImmersiveReader,
    NebulaIcon,
    NebulaTooltip,
  },
  props: {
    horizontalCanvasPadding: {
      type: Number,
      required: true,
    },
    verticalCanvasPadding: {
      type: Number,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    showImmersiveReader: Boolean,
  },
  computed: {
    ...mapState(useEditorStore, [
      'draftPointerAssetId',
      'domains',
    ]),
    ...mapState(useUserStore, [
      'isRtl',
    ]),
    assetId() {
      return this.draftPointerAssetId();
    },
    printPreviewLocation() {
      if (!this.assetId) return null;

      return `${this.domains.studioDomain}/print-preview?id=${this.assetId}&type=creation`;
    },
    floatingButtonHorizontalPosition() {
      return this.horizontalCanvasPadding !== 0 ? this.horizontalCanvasPadding : 40;
    },
    floatingButtonVerticalPosition() {
      return this.verticalCanvasPadding !== 0 ? this.verticalCanvasPadding : 40;
    },
  },
  methods: {
    showFullscreen() {
      bus.emit('requestFullscreen');
    },
  },
};
</script>

<style lang="stylus">
.editor-floating-buttons {
  display: flex;
  flex-wrap: nowrap;
  position: absolute;
  transform: translateY(-50%);
  z-index: 151;

  &__button-wrapper {
    display: block;
  }

  // We need a little extra specificity here for the player page
  .editor-floating-buttons__button, .immersive-reader__button {
    background-color: rgba($nebula-color-white, .9);

    .nebula-button__icon {
      fill: $nebula-color-neutral-500;
    }

    &:hover, &:focus {
      .nebula-button__icon {
        opacity: .5;
      }
    }

    &:active {
      background-color: $nebula-color-white;

      .nebula-button__icon {
        opacity: 1;
      }
    }
  }

  &__button--print {
    border-radius: $nebula-border-radius-button-default 0 0 $nebula-border-radius-button-default;
  }

  &__button--fullscreen {
    border-radius: 0 $nebula-border-radius-button-default $nebula-border-radius-button-default 0;
  }

  .immersive-reader__button {
    border-radius: 0;
  }

  html[dir="rtl"] & {
    &__button--print {
      border-radius: 0 $nebula-border-radius-button-default $nebula-border-radius-button-default 0;
    }

    &__button--fullscreen {
      border-radius: $nebula-border-radius-button-default 0 0 $nebula-border-radius-button-default;
    }
  }
}
</style>
